import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import {
  C, WEEK_DAYS, DEMO_ATTENTION_ITEMS, today,
  estRevenue, revenueAtRisk, noShowRiskCount, openSlots, pColor,
} from './frontDeskData';

export const CalendarView = ({ goOverview, calView, setCalView, patients, selectPatient }) => {
  const hours = Array.from({ length: 25 }, (_, i) => 7 + i * 0.5).filter(h => h <= 19).map(h => {
    const hr = Math.floor(h); const min = h % 1 === 0.5 ? '30' : '00';
    return { time24: h, label: `${hr > 12 ? hr - 12 : hr}:${min} ${hr >= 12 ? 'PM' : 'AM'}` };
  });

  const getSlotHour = (timeStr) => {
    const m = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return 9;
    let hr = parseInt(m[1]); const min = parseInt(m[2]); const ampm = m[3].toUpperCase();
    if (ampm === 'PM' && hr !== 12) hr += 12;
    if (ampm === 'AM' && hr === 12) hr = 0;
    return hr + min / 60;
  };

  const slotColor = (p) => p.stage === 'checked_out' ? { bg: '#f9f9f9', border: '#d1d5db' } :
    p.noShowRisk >= 25 ? { bg: '#fef2f2', border: C.red } :
    p.aiStatus.type === 'insurance_flagged' ? { bg: '#fef2f2', border: C.red } :
    p.stage === 'with_provider' ? { bg: '#eff6ff', border: '#3b82f6' } :
    p.copay.amount > 0 && !p.copay.collected ? { bg: '#fffbeb', border: C.amber } :
    p.aiStatus.type === 'confirmed' ? { bg: '#f0fdf4', border: C.green } :
    { bg: '#f0f0f0', border: '#d1d5db' };

  const openSlotTimes = ['11:30 AM', '4:00 PM'];

  return (
    <div data-testid="fd-calendar">
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '.5px solid #ebebeb', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={goOverview} style={{ fontSize: 11, color: C.purple, background: 'none', border: 'none', cursor: 'pointer' }} data-testid="fd-cal-back">&larr; Back to overview</button>
        <span style={{ fontSize: 14, fontWeight: 500, color: C.black }}>{today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        <div style={{ display: 'flex', gap: 2 }}>
          {['day','week','month'].map(v => (
            <button key={v} onClick={() => setCalView(v)}
              style={{ padding: '3px 10px', borderRadius: 4, background: calView === v ? C.black : '#f0f0f0', color: calView === v ? '#fff' : '#555', fontSize: 10, border: 'none', cursor: 'pointer', textTransform: 'capitalize' }}
              data-testid={`fd-cal-view-${v}`}>{v}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        {/* Calendar body */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 140px)' }}>
          {calView === 'day' && (
            <div style={{ padding: '0 20px 20px' }}>
              {hours.map((h, i) => {
                const appt = patients.find(p => Math.abs(getSlotHour(p.time) - h.time24) < 0.25);
                const isOpen = openSlotTimes.some(t => Math.abs(getSlotHour(t) - h.time24) < 0.25);
                return (
                  <div key={i} style={{ display: 'flex', minHeight: appt ? 'auto' : 28, borderBottom: '.5px solid #f5f5f5' }}>
                    <div style={{ width: 60, fontSize: 10, color: '#aaa', paddingTop: 6, flexShrink: 0 }}>{i % 2 === 0 ? h.label : ''}</div>
                    <div style={{ flex: 1, padding: '3px 0' }}>
                      {appt && (() => {
                        const sc = slotColor(appt);
                        return (
                          <div onClick={() => selectPatient(appt.id)}
                            style={{ background: sc.bg, borderLeft: `3px solid ${sc.border}`, borderRadius: 7, padding: '8px 10px', cursor: 'pointer', opacity: appt.stage === 'checked_out' ? 0.5 : 1 }}
                            data-testid={`fd-cal-slot-${appt.id}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: 11, fontWeight: 500, color: C.black }}>{appt.name}</span>
                              <span style={{ fontSize: 10, color: '#aaa' }}>{appt.type}</span>
                            </div>
                            <p style={{ fontSize: 9, color: appt.aiStatus.color }}>
                              {appt.aiStatus.type === 'confirmed' ? '\u2713' : '\u26a0'} AI: {appt.aiStatus.text}
                            </p>
                            {appt.copay.amount > 0 && !appt.copay.collected && (
                              <span style={{ fontSize: 9, color: C.amber, fontWeight: 500 }}>${appt.copay.amount} due</span>
                            )}
                          </div>
                        );
                      })()}
                      {isOpen && !appt && (
                        <div style={{ border: '1.5px dashed #d1d5db', borderRadius: 7, padding: '6px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                          data-testid={`fd-cal-open-${i}`}>
                          <div>
                            <span style={{ fontSize: 11, color: '#aaa' }}>{h.label} &mdash; Available</span>
                            <span style={{ fontSize: 9, color: C.purple, display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
                              <Sparkles style={{ width: 9, height: 9 }} /> AI: 3 matches
                            </span>
                            <span style={{ fontSize: 9, color: '#aaa' }}>This slot = ~$355 potential revenue</span>
                          </div>
                          <button onClick={() => toast.success('AI auto-filling slot')}
                            style={{ height: 24, padding: '0 8px', borderRadius: 4, background: C.black, color: '#fff', fontSize: 9, fontWeight: 500, border: 'none', cursor: 'pointer' }}>Auto-fill</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {calView === 'week' && (
            <div style={{ padding: '10px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {WEEK_DAYS.map((d, di) => (
                  <div key={di} style={{ textAlign: 'center' }}>
                    <div style={{ padding: '6px 0', background: d.isToday ? '#fafafa' : 'transparent', borderRadius: 6 }}>
                      <p style={{ fontSize: 9, color: '#aaa' }}>{d.day}</p>
                      <p style={{ fontSize: 13, fontWeight: 500, color: d.isToday ? C.black : '#555' }}>{d.date}</p>
                      <p style={{ fontSize: 9, color: '#aaa' }}>{d.appointments} appts</p>
                    </div>
                    {d.isToday && patients.slice(0, 4).map((p, pi) => {
                      const sc = slotColor(p);
                      return (
                        <div key={pi} onClick={() => selectPatient(p.id)}
                          style={{ background: sc.bg, borderLeft: `2px solid ${sc.border}`, borderRadius: 4, padding: '3px 5px', margin: '2px 0', cursor: 'pointer', textAlign: 'left' }}>
                          <span style={{ fontSize: 9, color: C.black, fontWeight: 500 }}>{p.name.split(' ')[0]}</span>
                        </div>
                      );
                    })}
                    {!d.isToday && Array.from({ length: Math.min(d.appointments, 3) }).map((_, pi) => (
                      <div key={pi} style={{ background: '#f0f0f0', borderLeft: '2px solid #d1d5db', borderRadius: 4, padding: '3px 5px', margin: '2px 0', textAlign: 'left' }}>
                        <span style={{ fontSize: 9, color: '#888' }}>Patient {pi + 1}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          {calView === 'month' && (
            <div style={{ padding: '10px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: 9, color: '#aaa', padding: 4 }}>{d}</div>
                ))}
                {Array.from({ length: 35 }).map((_, i) => {
                  const dayNum = i - (today.getDay() === 0 ? 6 : today.getDay() - 1) + today.getDate();
                  const isToday = dayNum === today.getDate();
                  const isValid = dayNum > 0 && dayNum <= 30;
                  return (
                    <div key={i} style={{ textAlign: 'center', padding: '8px 0', borderRadius: 4, background: isToday ? C.black : 'transparent', color: isToday ? '#fff' : isValid ? '#555' : '#ddd', fontSize: 12, cursor: isValid ? 'pointer' : 'default' }}>
                      {isValid ? dayNum : ''}
                      {isValid && dayNum <= today.getDate() && (
                        <div style={{ width: '80%', height: 3, borderRadius: 2, margin: '2px auto 0', background: isToday ? C.amber : C.green, opacity: 0.6 }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right rail — AI day intelligence */}
        {calView === 'day' && (
          <div style={{ width: 240, borderLeft: '.5px solid #ebebeb', padding: '14px', background: '#fff', overflowY: 'auto' }}>
            <p style={{ fontSize: 11, fontWeight: 500, color: C.black, marginBottom: 10 }}>AI day intelligence</p>
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: C.black }}>Today's revenue potential: ${estRevenue.toLocaleString()}</p>
              <p style={{ fontSize: 10, color: C.green }}>Confirmed: ${estRevenue - revenueAtRisk}</p>
              <p style={{ fontSize: 10, color: C.amber }}>At risk: ${revenueAtRisk}</p>
            </div>
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 10, fontWeight: 500, color: C.black, marginBottom: 3 }}>No-show management</p>
              <p style={{ fontSize: 10, color: '#555' }}>{noShowRiskCount} high-risk patients today</p>
              <p style={{ fontSize: 10, color: '#555' }}>AI sent {noShowRiskCount * 2} extra reminders</p>
              <p style={{ fontSize: 10, color: C.green }}>Est. savings vs. no action: $400</p>
            </div>
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 10, fontWeight: 500, color: C.black, marginBottom: 3 }}>Slot optimization</p>
              <p style={{ fontSize: 10, color: '#555' }}>{openSlots} open slots today</p>
              <p style={{ fontSize: 10, color: '#555' }}>AI has 3 waitlist matches ready</p>
              <button onClick={() => toast.success('Filling all slots')}
                style={{ height: 26, padding: '0 12px', borderRadius: 5, background: C.black, color: '#fff', fontSize: 10, fontWeight: 500, border: 'none', cursor: 'pointer', marginTop: 4 }}>Fill all</button>
            </div>
            <div>
              <p style={{ fontSize: 10, fontWeight: 500, color: C.black, marginBottom: 3 }}>Needs attention</p>
              {DEMO_ATTENTION_ITEMS.slice(0, 3).map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 5, padding: '4px 0', borderBottom: '.5px solid #f5f5f5' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: pColor(item.priority), flexShrink: 0, marginTop: 4 }} />
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 500, color: C.black }}>{item.patient}</p>
                    <p style={{ fontSize: 9, color: '#888' }}>{item.issue}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
